import { useEffect, useCallback } from 'react';

interface ShortcutAction {
  key: string;
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
  action: () => void;
  description: string;
}

/**
 * Global keyboard shortcuts hook
 * Usage: useKeyboardShortcuts(shortcuts, enabled)
 */
export function useKeyboardShortcuts(shortcuts: ShortcutAction[], enabled = true) {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!enabled) return;

    // Don't trigger shortcuts when typing in inputs
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      // Only allow Escape in inputs
      if (e.key !== 'Escape') return;
    }

    for (const shortcut of shortcuts) {
      const ctrlMatch = shortcut.ctrl ? (e.ctrlKey || e.metaKey) : true;
      const metaMatch = shortcut.meta ? e.metaKey : true;
      const shiftMatch = shortcut.shift ? e.shiftKey : !e.shiftKey;
      const keyMatch = e.key.toLowerCase() === shortcut.key.toLowerCase();

      if (keyMatch && ctrlMatch && metaMatch && shiftMatch) {
        e.preventDefault();
        shortcut.action();
        return;
      }
    }
  }, [shortcuts, enabled]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

/**
 * Common shortcut definitions
 */
export const GLOBAL_SHORTCUTS = {
  search: { key: 'k', ctrl: true, description: 'Open search' },
  escape: { key: 'Escape', description: 'Close modal / Go back' },
  newInvoice: { key: 'i', ctrl: true, shift: true, description: 'New Invoice' },
  newCustomer: { key: 'n', ctrl: true, shift: true, description: 'New Customer' },
  newExpense: { key: 'e', ctrl: true, shift: true, description: 'New Expense' },
  save: { key: 's', ctrl: true, description: 'Save' },
  dashboard: { key: '1', ctrl: true, description: 'Go to Dashboard' },
  customers: { key: '2', ctrl: true, description: 'Go to Customers' },
  invoices: { key: '3', ctrl: true, description: 'Go to Invoices' },
  settings: { key: ',', ctrl: true, description: 'Open Settings' },
};
